import type { FieldErrors, FieldValues } from 'react-hook-form'
import { startCase } from '@axilon-apps/libs-shared-utils'

export interface FieldNameMap {
  [fieldPath: string]: string
}

// Non-greedy first group so nested field-arrays group under the OUTERMOST
// collection (e.g. `configurations.0.rules.1.name` → the `configurations` item).
const INDEXED_PATH_RE = /^(.+?)\.(\d+)\.(.+)$/

// Collect the paths of every leaf error (those with a `message`), skipping
// react-hook-form's `ref`/`root` bookkeeping keys and recursing through nested
// objects and field-arrays alike.
const collectErrorPaths = (errs: Record<string, unknown>, prefix = ''): string[] =>
  Object.entries(errs).flatMap(([key, error]) => {
    if (key === 'ref' || key === 'root' || !error || typeof error !== 'object') return []
    const path = prefix ? `${prefix}.${key}` : key
    if ('message' in error) return [path]
    return collectErrorPaths(error as Record<string, unknown>, path)
  })

const labelFor = (path: string, key: string, map?: FieldNameMap): string =>
  map?.[path] ?? map?.[key] ?? startCase(key)

export const getErrorFieldNames = <TFieldValues extends FieldValues = FieldValues>(
  errors: FieldErrors<TFieldValues>,
  fieldNameMap?: FieldNameMap
): string[] => {
  const groups: string[] = []
  const groupIndex = new Map<string, number>()

  collectErrorPaths(errors as Record<string, unknown>).forEach((path) => {
    const match = path.match(INDEXED_PATH_RE)
    if (!match) {
      groups.push(labelFor(path, path.split('.').pop() ?? path, fieldNameMap))
      return
    }

    const [, collectionKey, indexStr, fieldKey] = match
    const groupKey = `${collectionKey}.${indexStr}`
    const heading = `${fieldNameMap?.[collectionKey] ?? startCase(collectionKey)} ${Number(indexStr) + 1}`
    const fieldLabel = labelFor(path, fieldKey, fieldNameMap)

    const existing = groupIndex.get(groupKey)
    if (existing === undefined) {
      groupIndex.set(groupKey, groups.length)
      groups.push(`${heading}: ${fieldLabel}`)
    } else {
      groups[existing] += `, ${fieldLabel}`
    }
  })

  return groups
}
